/**
 * WriteoffSlice
 * 
 * Redux slice for managing writeoff state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - extraReducers.ts: API endpoint matchers
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, WriteoffSliceState } from './state';
import { buildWriteoffExtraReducers } from './extraReducers';

// Import all reducers
import {
  updateWriteoff,
  setCurrentWriteoff,
  updateCurrentWriteoff,
  createNewWriteoff,
  clearCurrentWriteoff,
  forceResetWriteoff,
  restoreFromBackup,
  clearBackup,
  updateWriteoffIdentifiers,
  updateWriteoffPatient,
  updateWriteoffDetail,
  addWriteoffNote,
  updateWriteoffNote,
  removeWriteoffNote,
  addApproval,
  updateApproval,
  markFieldAsEdited,
  clearEditedFields,
  markAsSaved,
  discardChanges,
  setValidationError,
  clearValidationError,
  clearAllValidationErrors,
} from './reducers';

/**
 * Writeoff slice definition
 */
const writeoffSlice = createSlice({
  name: 'writeoff',
  initialState,
  reducers: {
    // Core reducers
    updateWriteoff,
    setCurrentWriteoff,
    updateCurrentWriteoff,
    createNewWriteoff,
    clearCurrentWriteoff,
    forceResetWriteoff,
    restoreFromBackup,
    clearBackup,
    
    // Sub-entity reducers
    updateWriteoffIdentifiers,
    updateWriteoffPatient,
    updateWriteoffDetail,
    
    // Notes reducers
    addWriteoffNote,
    updateWriteoffNote,
    removeWriteoffNote,
    
    // Approvals reducers
    addApproval,
    updateApproval,
    
    // Edit tracking reducers
    markFieldAsEdited,
    clearEditedFields,
    markAsSaved,
    discardChanges,
    
    // Validation reducers
    setValidationError,
    clearValidationError,
    clearAllValidationErrors,
  },
  extraReducers: buildWriteoffExtraReducers,
});

// Export actions
export const writeoffActions = writeoffSlice.actions;

// Export individual actions for convenience
export const {
  updateWriteoff: updateWriteoffAction,
  setCurrentWriteoff: setCurrentWriteoffAction,
  updateCurrentWriteoff: updateCurrentWriteoffAction,
  createNewWriteoff: createNewWriteoffAction,
  clearCurrentWriteoff: clearCurrentWriteoffAction,
  forceResetWriteoff: forceResetWriteoffAction,
  restoreFromBackup: restoreFromBackupAction,
  clearBackup: clearBackupAction,
  updateWriteoffIdentifiers: updateWriteoffIdentifiersAction,
  updateWriteoffPatient: updateWriteoffPatientAction,
  updateWriteoffDetail: updateWriteoffDetailAction,
  addWriteoffNote: addWriteoffNoteAction,
  updateWriteoffNote: updateWriteoffNoteAction,
  removeWriteoffNote: removeWriteoffNoteAction,
  addApproval: addApprovalAction,
  updateApproval: updateApprovalAction,
  markFieldAsEdited: markFieldAsEditedAction,
  clearEditedFields: clearEditedFieldsAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
  setValidationError: setValidationErrorAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = writeoffSlice.actions;

// Export reducer
export default writeoffSlice.reducer;

// Re-export state types
export type { WriteoffSliceState } from './state';

// Re-export selectors
export * from './selectors';
